import React, { useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CheckIcon, PlusIcon, ShoppingCartIcon } from 'lucide-react';
const MAIN_PRICE = 4.99;
import { getCheckoutUrl } from '../utils/getCheckoutUrl';
import { trackAddToCart } from '../utils/metaPixel';
const bumps = [{
  id: 'ia',
  price: 2.99,
  link: 'https://ideas-que-transforman-2.myshopify.com/products/como-ganar-dinero-usando-inteligencia-artificial',
  image: 'https://cdn.magicpatterns.com/uploads/wfRNzvAb4FCDTiM1K9wKTq/ChatGPT_Image_1_abr_2026,_01_28_44_p.m..png',
  emoji: '💰',
  label: 'Oferta especial #1 — Añade a tu compra',
  title: '5 formas reales de generar ingresos con IA (elige la más fácil para ti)',
  description: 'Elige la forma más rápida para empezar según tu situación. Incluye ideas simples, pasos claros y ejemplos reales.',
  cta: '👉 Elige la forma más rápida para empezar.',
  summaryName: 'Ingresos con IA desde cero'
}, {
  id: 'mensajes',
  price: 2.99,
  link: 'https://ideas-que-transforman-2.myshopify.com/products/te-estan-ignorando-y-te-esta-costando-dinero',
  image: 'https://cdn.magicpatterns.com/uploads/jW2g56jwBum7ALyVZKj36M/ChatGPT_Image_1_abr_2026,_01_02_36_p.m..png',
  emoji: '🚀',
  label: 'Oferta especial #2 — Añade a tu compra',
  title: 'Deja de ser ignorado y empieza a recibir respuestas',
  description: 'Mensajes probados que convierten conversaciones en ventas. Listos para copiar y pegar.',
  cta: '👉 Mensajes probados que convierten conversaciones en ventas.',
  summaryName: 'Mensajes que generan ventas'
}, {
  id: 'clientes',
  price: 2.99,
  link: 'https://ideas-que-transforman-2.myshopify.com/products/donde-encontrar-clientes-que-paguen-aunque-estes-empezando',
  image: 'https://cdn.magicpatterns.com/uploads/8aQBNhugoizHwokWoEX9n7/ab9cbb1b-917b-4c77-ac34-1e7fb77c1813.png',
  emoji: '🎯',
  label: 'Oferta especial #3 — Añade a tu compra',
  title: 'Dónde encontrar clientes que paguen (aunque estés empezando)',
  description: 'Aquí es donde realmente empieza el dinero. Descubre dónde están tus primeros clientes.',
  cta: '👉 Aquí es donde realmente empieza el dinero.',
  summaryName: 'Encontrar clientes que paguen'
}];
interface OrderBumpProps {
  addedBumps: Record<string, boolean>;
  toggleBump: (id: string) => void;
}
export function OrderBump({
  addedBumps,
  toggleBump
}: OrderBumpProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const bumpTotal = bumps.reduce((sum, bump) => sum + (addedBumps[bump.id] ? bump.price : 0), 0);
  const total = MAIN_PRICE + bumpTotal;
  const handleCheckout = () => {
    trackAddToCart();
    window.open(getCheckoutUrl(addedBumps), '_blank');
  };
  return <section ref={ref} className="py-16 px-6 bg-dark-bg" data-id="element-69">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.6
    }} className="max-w-2xl mx-auto space-y-6" data-id="element-70">
        {/* Bump Cards */}
        {bumps.map((bump, index) => {
        const isAdded = !!addedBumps[bump.id];
        return <motion.div key={bump.id} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className={`relative rounded-xl border-2 transition-all duration-300 overflow-hidden ${isAdded ? 'border-accent shadow-lg shadow-accent/10' : 'border-dark-tertiary hover:border-accent/40'}`} data-id="element-71">
              {/* Header strip */}
              <div className="bg-accent/10 px-6 py-3 flex items-center gap-2" data-id="element-72">
                <span className="text-lg" data-id="element-73">{bump.emoji}</span>
                <span className="text-accent font-semibold text-sm uppercase tracking-wide" data-id="element-74">
                  {bump.label}
                </span>
              </div>

              <div className="p-6" data-id="element-75">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-76">
                  {/* Clickable Image */}
                  <button onClick={() => toggleBump(bump.id)} className="relative flex-shrink-0 w-full md:w-48 h-40 md:h-auto rounded-lg overflow-hidden group cursor-pointer" data-id="element-77">
                    <img src={bump.image} alt={bump.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-78" />
                    <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${isAdded ? 'bg-accent/20' : 'bg-dark-bg/40 group-hover:bg-dark-bg/20'}`} data-id="element-79">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isAdded ? 'bg-accent text-dark-bg scale-100' : 'bg-dark-bg/70 text-text-secondary scale-90 group-hover:scale-100'}`} data-id="element-80">
                        {isAdded ? <CheckIcon className="w-6 h-6" data-id="element-81" /> : <PlusIcon className="w-6 h-6" data-id="element-82" />}
                      </div>
                    </div>
                  </button>

                  {/* Content */}
                  <div className="flex-1" data-id="element-83">
                    <div className="flex items-start justify-between gap-4 mb-3" data-id="element-84">
                      <h3 className="text-xl font-serif font-bold text-text-primary leading-tight" data-id="element-85">
                        {bump.title}
                      </h3>
                      <span className="text-accent font-bold text-xl flex-shrink-0" data-id="element-86">
                        €{bump.price.toFixed(2)}
                      </span>
                    </div>

                    <p className="text-text-secondary text-sm leading-relaxed mb-4" data-id="element-87">
                      {bump.description}
                    </p>

                    <p className="text-accent/80 text-sm italic" data-id="element-88">{bump.cta}</p>

                    <button onClick={() => toggleBump(bump.id)} className={`mt-4 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${isAdded ? 'bg-accent/20 text-accent border border-accent/30' : 'bg-dark-tertiary text-text-secondary hover:bg-dark-tertiary/80 border border-dark-tertiary'}`} data-id="element-89">
                      {isAdded ? '✓ Añadido a tu compra' : `+ Añadir por solo €${bump.price.toFixed(2)}`}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>;
      })}

        {/* Price Summary */}
        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="p-6 bg-dark-secondary rounded-xl border border-dark-tertiary" data-id="element-90">
          <div className="space-y-3 mb-6" data-id="element-91">
            <div className="flex items-center justify-between" data-id="element-92">
              <span className="text-text-secondary" data-id="element-93">Ebook principal</span>
              <div className="flex items-center gap-3" data-id="element-94">
                <span className="text-text-secondary/50 line-through text-sm" data-id="element-95">
                  €49.99
                </span>
                <span className="text-text-primary font-semibold" data-id="element-96">€4.99</span>
              </div>
            </div>

            <AnimatePresence data-id="element-97">
              {bumps.map(bump => addedBumps[bump.id] && <motion.div key={bump.id} initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="flex items-center justify-between overflow-hidden" data-id="element-98">
                      <span className="text-text-secondary" data-id="element-99">
                        {bump.summaryName}
                      </span>
                      <span className="text-text-primary font-semibold" data-id="element-100">
                        €{bump.price.toFixed(2)}
                      </span>
                    </motion.div>)}
            </AnimatePresence>

            <div className="border-t border-dark-tertiary pt-3 flex items-center justify-between" data-id="element-101">
              <span className="text-text-primary font-bold text-lg" data-id="element-102">Total</span>
              <motion.span key={total} initial={{
              scale: 1.2,
              color: '#e8a838'
            }} animate={{
              scale: 1,
              color: '#e8a838'
            }} className="text-accent font-bold text-2xl" data-id="element-103">
                €{total.toFixed(2)}
              </motion.span>
            </div>
          </div>

          <button onClick={handleCheckout} className="w-full py-5 bg-accent text-dark-bg font-bold text-lg rounded-lg hover:bg-accent/90 transition-all shadow-lg shadow-accent/20 flex items-center justify-center gap-3" data-id="element-104">
            <ShoppingCartIcon className="w-5 h-5" data-id="element-105" />
            Comprar ahora — €{total.toFixed(2)}
          </button>
        </motion.div>
      </motion.div>
    </section>;
}